import { useState } from "react";
import { Link } from "react-router-dom";
import { WorkspaceCardData } from "./Interfaces/WorkspaceCard";
import workspaceTypes from "../assets/WorkspaceTypes";
import { FaUsers, FaParking } from "react-icons/fa";
import { TbRulerMeasure } from "react-icons/tb";

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function WorkspaceCard(workspace: WorkspaceCardData) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [currentImg, setCurrentImg] = useState(0);

  const typeData = workspaceTypes.find(
    (t) => t.title === workspace.workspaceType,
  );
  const IconComponent = typeData?.icon || null;

  const bestPrice = workspace.pricePerYear
    ? { value: workspace.pricePerYear, label: "/yr" }
    : null;

  return (
    <Link
      to={`/workspace/${workspace.workspaceId}`}
      className="group block rounded-2xl overflow-hidden bg-white h-full flex flex-col cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      {/* Image Section */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        {!imgLoaded && (
          <div className="absolute inset-0 animate-pulse bg-gray-200" />
        )}
        <img
          src={workspace.images[currentImg]?.imageUrl}
          alt={workspace.title}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          onLoad={() => setImgLoaded(true)}
        />

        {/* Image dots */}
        {workspace.images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {workspace.images.slice(0, 5).map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentImg(i);
                }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  i === currentImg
                    ? "bg-white w-3"
                    : "bg-white/60 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        )}

        {/* Type badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-800 shadow-sm">
          {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
          {workspace.workspaceType}
        </div>

        {/* Furnished badge */}
        {workspace.isFurnished && (
          <div className="absolute top-3 right-3 px-2.5 py-1 bg-green-500/90 backdrop-blur-sm rounded-full text-xs font-medium text-white shadow-sm">
            Furnished
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex-grow flex flex-col">
        {/* Title & Location */}
        <h2 className="text-base font-semibold text-gray-900 line-clamp-1 mb-1 group-hover:text-[var(--secondary)] transition-colors">
          {workspace.title}
        </h2>
        <p className="text-sm text-gray-500 line-clamp-1 mb-3">
          {workspace.address}, {workspace.city}, {workspace.state}
        </p>

        {/* Quick stats */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
          <span className="inline-flex items-center gap-1">
            <FaUsers className="w-3 h-3" />
            {workspace.capacity}
          </span>
          <span className="inline-flex items-center gap-1">
            <TbRulerMeasure className="w-3 h-3" />
            {workspace.sizeSqm} m²
          </span>
          {workspace.parkingSpotsTotal ? (
            <span className="inline-flex items-center gap-1">
              <FaParking className="w-3 h-3" />
              {workspace.parkingSpotsTotal}
            </span>
          ) : null}
          {workspace.buildingClass ? (
            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-medium text-gray-600">
              Class {workspace.buildingClass}
            </span>
          ) : null}
        </div>

        {/* Price */}
        <div className="mt-auto pt-3 border-t border-gray-100 flex items-baseline justify-between">
          {bestPrice ? (
            <div>
              <span className="text-lg font-bold text-gray-900">
                {formatCurrency(bestPrice.value)}
              </span>
              <span className="text-sm text-gray-500">{bestPrice.label}</span>
            </div>
          ) : (
            <span className="text-sm text-gray-400">Contact for pricing</span>
          )}
          {workspace.yearBuilt ? (
            <span className="text-xs text-gray-400">
              Built {workspace.yearBuilt}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
